import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Bundle, Course, Tag } from '../../types';
import { GroupingConfig, shelfCardLabel, courseTagIn, bundlesMatchingTag } from '../../lib/catalogGrouping';
import { PackCard } from '../catalog/GroupedCatalog';
import { SchoolPassScope, bundleCoveringPass, hasAllAccessPass } from '../../services/schoolPassService';
import { AnimatedSection } from '../ui/AnimatedSection';

/**
 * Home sections for grouped-catalogue tenants (branding.catalog set) — the
 * "dashboard first, the shop is a doorway" half of the decided IA. Loose
 * in-progress work renders as a grouped continue-list instead of large media
 * rows, and the doorway band replaces Home's full bundle section + course
 * grid (the catalogue lives on /catalog — one place, one source of truth).
 * Design + decision record: design-explorations/student-ia-revise/.
 */

const EASE = [0.22, 1, 0.36, 1] as const;

export type ContinueItem = Course & {
  progress: number;
  lastLessonId?: string;
};

const primaryTagOf = (course: Course, config: GroupingConfig): Tag | null =>
  courseTagIn(course, config.primary);

const secondaryTagOf = (course: Course, config: GroupingConfig): Tag | null =>
  courseTagIn(course, config.secondary);

const ContinueRow: React.FC<{ item: ContinueItem; config: GroupingConfig; groupTag: Tag | null }> = ({
  item,
  config,
  groupTag,
}) => {
  const primary = primaryTagOf(item, config);
  const { slot, title } = shelfCardLabel(item.title, [primary, groupTag]);
  const target = item.lastLessonId ? `/learn/${item.id}/lesson/${item.lastLessonId}` : `/enroll/${item.id}`;
  return (
    <Link
      to={target}
      className="group flex items-center gap-3 rounded-xl bg-white ring-1 ring-paper-line px-4 py-3 hover:shadow-card-hover transition-shadow"
    >
      {slot && (
        <span className="inline-flex flex-shrink-0 rounded-md bg-paper-2 px-2 py-0.5 text-[10px] font-extrabold tracking-wider text-gray-700">
          {slot}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
        {title}
      </span>
      {primary && (
        <span className="hidden sm:inline flex-shrink-0 text-xs text-gray-400">{primary.name}</span>
      )}
      <span className="hidden sm:block h-1.5 w-24 flex-shrink-0 overflow-hidden rounded-full bg-paper-2">
        <motion.span
          className="block h-full rounded-full bg-primary-500"
          initial={{ width: 0 }}
          animate={{ width: `${item.progress}%` }}
          transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
        />
      </span>
      <span className="w-9 flex-shrink-0 text-right text-xs font-extrabold text-gray-700 tabular-nums">
        {item.progress}%
      </span>
    </Link>
  );
};

/**
 * Loose in-progress enrollments grouped by the secondary axis (papers), rows
 * marked with their question slot — the wireframe's "Your labs" list. Items
 * without a secondary tag land in a trailing group; nothing vanishes.
 */
export const GroupedContinueList: React.FC<{ items: ContinueItem[]; config: GroupingConfig }> = ({
  items,
  config,
}) => {
  if (items.length === 0) return null;
  const byOrder = (a: ContinueItem, b: ContinueItem) =>
    (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.title.localeCompare(b.title);

  const groups: Array<{ tag: Tag | null; rows: ContinueItem[] }> = [];
  if (config.secondary) {
    for (const tag of config.secondary.tags) {
      const rows = items.filter((i) => secondaryTagOf(i, config)?.id === tag.id).sort(byOrder);
      if (rows.length > 0) groups.push({ tag, rows });
    }
    const rest = items.filter((i) => secondaryTagOf(i, config) === null).sort(byOrder);
    if (rest.length > 0) groups.push({ tag: null, rows: rest });
  } else {
    groups.push({ tag: null, rows: [...items].sort(byOrder) });
  }

  return (
    <AnimatedSection>
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight mb-4">In progress</h2>
        <div className="rounded-2xl bg-paper-2 border border-paper-line p-4 sm:p-5">
          {groups.map((group, gi) => (
            <div key={group.tag?.id ?? 'rest'}>
              {(group.tag || groups.length > 1) && (
                <div
                  className={`mb-2 text-[11px] font-extrabold uppercase tracking-[0.12em] text-gray-400 ${gi > 0 ? 'mt-4' : ''}`}
                >
                  {group.tag?.name ?? 'More'}
                </div>
              )}
              <div className="space-y-2">
                {group.rows.map((item) => (
                  <ContinueRow key={item.id} item={item} config={config} groupTag={group.tag} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </AnimatedSection>
  );
};

/**
 * The doorway band: paid unowned packs as compact cards, a free-courses tile
 * when the tenant sells, and the browse-all link into the catalogue. Replaces
 * Home's full bundle cards + all-courses grid for grouped tenants.
 */
export const ExploreDoorways: React.FC<{
  bundles: Bundle[];
  freeCourseCount: number;
  showPrice: boolean;
  /** Full course list + config so a paper pack can route into its pivot. */
  courses: Course[];
  config: GroupingConfig;
  /** The learner's active school passes — a covered pack names the school, not a price. */
  schoolPasses?: SchoolPassScope[];
  /** Pass-holding students see no prices: only covered packs are offered. */
  hidePrices?: boolean;
}> = ({ bundles, freeCourseCount, showPrice, courses, config, schoolPasses = [], hidePrices = false }) => {
  // Same as the catalogue's strip: nothing to get when an all-access pass covers every pack.
  const paid =
    showPrice && !hasAllAccessPass(schoolPasses)
      ? bundles.filter(
          (b) => b.price > 0 && (!hidePrices || !!bundleCoveringPass(schoolPasses, b.id)),
        )
      : [];

  // A pack wholly inside one secondary tag (a "Paper 1 pack") doors into the
  // pivoted catalogue — see everything for that paper with the pack's CTA in
  // the shelf header — rather than straight to checkout. Packs that span
  // papers keep linking to their own page. (The decided design's "routes into
  // the catalogue with the paper pre-selected".)
  const pivotTargetFor = (bundle: Bundle): string | undefined => {
    for (const tag of config.secondary?.tags ?? []) {
      if (bundlesMatchingTag([bundle], courses, tag).length > 0) {
        return `/catalog?pivot=${tag.id}`;
      }
    }
    return undefined;
  };
  return (
    <AnimatedSection>
      <section className="mb-12">
        {paid.length > 0 && (
          <>
            <h2 className="text-sm font-bold uppercase tracking-[0.12em] text-gray-500 mb-3">
              Get the full set
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              {paid.map((b) => (
                <PackCard
                  key={b.id}
                  bundle={b}
                  to={pivotTargetFor(b)}
                  coveredBy={bundleCoveringPass(schoolPasses, b.id)?.schoolName}
                />
              ))}
            </div>
          </>
        )}
        {showPrice && freeCourseCount > 0 && (
          <Link
            to="/catalog"
            className="group mb-3 flex items-center justify-between gap-3 rounded-xl bg-white ring-1 ring-paper-line px-4 py-3 hover:shadow-card-hover transition-shadow"
          >
            <div>
              <div className="text-sm font-bold text-gray-900 group-hover:text-primary-600 transition-colors">
                Start free
              </div>
              <div className="text-xs text-gray-500">
                {freeCourseCount} free {freeCourseCount === 1 ? 'course' : 'courses'} · no payment needed
              </div>
            </div>
            <span className="flex-shrink-0 text-xs font-bold text-green-600">FREE</span>
          </Link>
        )}
        <Link
          to="/catalog"
          className="group flex items-center justify-between rounded-xl border border-dashed border-paper-line px-4 py-3 text-sm font-semibold text-gray-600 hover:text-gray-900 hover:border-gray-300 transition-colors"
        >
          Browse all courses
          <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-primary-600 transition-colors" />
        </Link>
      </section>
    </AnimatedSection>
  );
};

/**
 * "Practice Lab · Paper 1 · Q1" — the hero's context line, replacing the
 * information the trimmed title no longer carries. Null when the course has
 * no group tags (caller falls back to the untrimmed title).
 */
export function heroContext(
  course: Course,
  config: GroupingConfig,
): { context: string; title: string } | null {
  const primary = primaryTagOf(course, config);
  const secondary = secondaryTagOf(course, config);
  if (!primary && !secondary) return null;
  const { slot, title } = shelfCardLabel(course.title, [primary, secondary]);
  const context = [primary?.name, secondary?.name, slot].filter(Boolean).join(' · ');
  return { context, title };
}
